import { LogOut, Menu as MenuIcon, Monitor, Moon, PanelLeftClose, PanelLeftOpen, Search, Sun, UserCog } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth, useUser } from '@/app/auth';
import { NAV } from '@/app/nav';
import { useTheme } from '@/app/theme';
import { Avatar, Button, Kbd, LoadingBlock, Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui';
import { cn } from '@/lib/cn';
import { LifecycleBanner } from '@/modules/signup/lifecycle-banner';
import { moduleForPath, useCanSee, useEntitlements, useModuleEnabled } from '@/modules/subscription/api'; // Phase I (I-B, I2); P2-0: useCanSee
import { UpgradePrompt } from '@/modules/subscription/upgrade-prompt';
import { SupportBanner } from '@/modules/platform-console/support-banner'; // Phase I (I-D)
import { CommandPalette } from './command-palette';
import { NewMenu } from './new-menu';
import { NotificationsBell } from './notifications';

const COLLAPSED_KEY = 'denwen-msp.sidebar.collapsed';

/** Staff app frame: dark left sidebar (HaloPSA/IT Glue), top bar with search and the New menu (CREATE_ACTIONS). */
export function AppShell() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  // Phase I (I-B): a module the plan leaves out shows an upgrade prompt instead of its pages.
  const moduleEnabled = useModuleEnabled();
  const entitlements = useEntitlements();
  const gatedModule = moduleForPath(location.pathname);

  // Close the mobile nav after navigating (adjusting state during render, not in an effect).
  const [navPath, setNavPath] = useState(location.pathname);
  if (navPath !== location.pathname) {
    setNavPath(location.pathname);
    setMobileOpen(false);
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });

  return (
    <div className="flex h-full">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>
      {mobileOpen ? <div className="fixed inset-0 z-30 bg-slate-950/40 lg:hidden" onClick={() => setMobileOpen(false)} /> : null}
      <Sidebar collapsed={collapsed} mobileOpen={mobileOpen} onToggle={toggle} />
      <div className="flex min-w-0 flex-1 flex-col">
        <SupportBanner />
        <Topbar onMenu={() => setMobileOpen(true)} onSearch={() => setPaletteOpen(true)} />
        {/* `relative`: absolutely positioned content (sr-only inputs and labels) is placed and
            clipped inside this scroller. Without it, one below the fold stretched the document,
            and the page grew a second scrollbar onto blank space. */}
        <main id="main" className="relative min-h-0 flex-1 overflow-y-auto">
          <LifecycleBanner />
          {gatedModule && entitlements.isLoading ? <LoadingBlock /> : gatedModule && !moduleEnabled(gatedModule) ? <UpgradePrompt module={gatedModule} /> : <Outlet />}
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

function Sidebar({ collapsed, mobileOpen, onToggle }: { collapsed: boolean; mobileOpen: boolean; onToggle: () => void }) {
  const canSee = useCanSee();
  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 flex flex-col bg-sidebar text-sidebar-fg transition-[width,transform] duration-150 lg:static lg:translate-x-0',
        collapsed ? 'lg:w-[60px]' : 'lg:w-60',
        mobileOpen ? 'w-60 translate-x-0' : 'w-60 -translate-x-full',
      )}
    >
      <div className={cn('flex h-14 items-center gap-2 px-4', collapsed && 'lg:justify-center lg:px-0')}>
        <img src="/favicon.svg" alt="" className="size-7" />
        <span className={cn('text-[15px] font-semibold tracking-tight', collapsed && 'lg:hidden')}>Denwen MSP</span>
      </div>
      <nav aria-label="Main" className="flex-1 overflow-y-auto px-2 pb-4">
        {NAV.map((group, gi) => {
          const items = group.items.filter(canSee);
          if (!items.length) return null;
          return (
            <div key={gi} className="mt-3 first:mt-0">
              {group.section ? (
                <div className={cn('px-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted', collapsed && 'lg:invisible lg:h-2')}>{group.section}</div>
              ) : null}
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'flex h-8 items-center gap-2.5 rounded-md px-2 text-[13px] font-medium text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-fg',
                      isActive && 'bg-sidebar-active text-sidebar-fg',
                      collapsed && 'lg:justify-center lg:px-0',
                    )
                  }
                >
                  <item.icon className="size-4 shrink-0" aria-hidden />
                  <span className={cn('truncate', collapsed && 'lg:sr-only')}>{item.label}</span>
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      <button
        type="button"
        onClick={onToggle}
        className="hidden h-10 items-center gap-2 border-t border-white/5 px-4 text-xs text-sidebar-muted hover:text-sidebar-fg lg:flex"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <PanelLeftOpen className="mx-auto size-4" /> : <PanelLeftClose className="size-4" />}
        {!collapsed ? 'Collapse' : null}
      </button>
    </aside>
  );
}

function Topbar({ onMenu, onSearch }: { onMenu: () => void; onSearch: () => void }) {
  const user = useUser();
  const { signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b bg-surface px-3 sm:px-4">
      <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu" onClick={onMenu}>
        <MenuIcon />
      </Button>
      <button
        type="button"
        onClick={onSearch}
        className="flex h-8 w-full max-w-md items-center gap-2 rounded-md border bg-surface-2 px-2.5 text-sm text-subtle transition-colors hover:border-border-strong"
      >
        <Search className="size-4" />
        <span className="flex-1 text-left">Search organizations, tickets, assets…</span>
        <Kbd>{isMac ? '⌘' : 'Ctrl'} K</Kbd>
      </button>
      <div className="ml-auto flex items-center gap-1.5">
        <NewMenu />
        <NotificationsBell />
        <Menu
          trigger={
            <button type="button" className="flex items-center gap-2 rounded-md p-1 hover:bg-surface-hover" aria-label="Account menu">
              <Avatar name={user.name} />
            </button>
          }
        >
          <div className="px-2 py-1.5">
            <div className="text-sm font-medium">{user.name}</div>
            <div className="text-xs text-muted">
              {user.email} · {user.roleName}
            </div>
          </div>
          <MenuSeparator />
          <MenuItem icon={<UserCog />} onSelect={() => navigate('/account')}>
            Account & security
          </MenuItem>
          <MenuSeparator />
          <MenuLabel>Theme</MenuLabel>
          <MenuItem icon={<Sun />} onSelect={() => setTheme('light')}>
            Light {theme === 'light' ? '✓' : ''}
          </MenuItem>
          <MenuItem icon={<Moon />} onSelect={() => setTheme('dark')}>
            Dark {theme === 'dark' ? '✓' : ''}
          </MenuItem>
          <MenuItem icon={<Monitor />} onSelect={() => setTheme('system')}>
            System {theme === 'system' ? '✓' : ''}
          </MenuItem>
          <MenuSeparator />
          <MenuItem icon={<LogOut />} onSelect={() => void signOut().then(() => navigate('/login'))}>
            Sign out
          </MenuItem>
        </Menu>
      </div>
    </header>
  );
}

/** Standard page padding + max width. Every staff page renders inside one. */
export const Page = ({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) => (
  <div className={cn('mx-auto w-full px-4 py-5 sm:px-6', wide ? 'max-w-[1600px]' : 'max-w-[1280px]', className)}>{children}</div>
);

export { Link };
